/**
 * Record goods receipt — composer (against a PO; pre-fills ordered lines).
 *
 * Accepts an optional `?po=<uuid>` search param to preselect the purchase
 * order — the deep-link target for a PO detail's "Receive goods" action.
 *
 * @see prototype/purchases/goods-receipt-new.html — V1.1 composer baseline
 */
import { createFileRoute, notFound } from "@tanstack/react-router";
import { hasPermission } from "@flint/api-contract";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { GrnComposerPage } from "../../../../features/goods-receipts/index.ts";
import { meQueryOptions } from "../../../../lib/session.ts";

interface NewGrnSearch {
  po?: string;
}

export const Route = createFileRoute("/(workspace)/procurement/goods-receipts/new")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "goodsReceipts.manage")) {
      throw notFound();
    }
  },
  component: NewGoodsReceiptRoute,
  validateSearch: (s: Record<string, unknown>): NewGrnSearch => {
    const out: NewGrnSearch = {};
    if (typeof s.po === "string" && s.po.length > 0) out.po = s.po;
    return out;
  },
});

function NewGoodsReceiptRoute() {
  const search = Route.useSearch();
  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Procurement", to: "/" },
        { label: "Goods receipts", to: "/procurement/goods-receipts" },
        { label: "New" },
      ]}
      contentClassName="max-w-none"
      viewportClassName="p-0 md:p-0"
    >
      <GrnComposerPage {...(search.po ? { preselectPoId: search.po } : {})} />
    </WorkspaceShell>
  );
}
